<template>
  <div class="csv-import-page">
    <div class="page-header">
      <div class="header-content">
        <div class="header-left">
          <h1 class="page-title">
            <v-icon size="28" class="mr-3">mdi-file-delimited-outline</v-icon>
            Importar usuarios desde CSV
          </h1>
          <p class="page-subtitle">Carga masiva de usuarios mediante archivos CSV</p>
        </div>

        <div class="header-actions">
          <v-btn
            v-if="step !== 'upload'"
            @click="resetToUpload"
            color="grey"
            variant="outlined"
            prepend-icon="mdi-arrow-left"
            class="mr-3"
          >
            Volver al inicio
          </v-btn>
          <v-btn
            @click="downloadTemplate"
            color="primary"
            variant="tonal"
            prepend-icon="mdi-download"
          >
            Descargar plantilla
          </v-btn>
        </div>
      </div>
    </div>

    <!-- Stepper -->
    <v-sheet class="stepper mb-6">
      <div class="step-indicators">
        <div v-for="(s, index) in steps" :key="index" class="step-item" :class="getStepClass(index)">
          <div class="step-circle" @click="goToStep(index)">
            <v-icon v-if="isStepCompleted(index)" size="18">mdi-check</v-icon>
            <span v-else>{{ index + 1 }}</span>
          </div>
          <div class="step-label">{{ s }}</div>
        </div>
        <div class="step-line" :style="{ width: stepProgress + '%' }"></div>
      </div>
    </v-sheet>

    <!-- Main Content -->
    <v-card class="main-card" elevation="0">
      <!-- Step 1: Upload -->
      <v-slide-y-reverse-transition mode="out-in" hide-on-leave>
        <div v-if="step === 'upload'" key="upload">
          <v-card-text class="pa-6">
            <CsvUploader
              :file="csvFile"
              :error="csvError"
              @file-selected="setFile"
              @clear="clearFile"
            />
          </v-card-text>

          <v-card-text v-if="csvFile" class="pa-6 pt-0">
            <v-card color="primary" variant="tonal" class="pa-5 text-center">
              <v-icon size="40" color="primary" class="mb-2">mdi-file-delimited-outline</v-icon>
              <p class="text-body-1 font-weight-medium text-primary mb-1">{{ csvFile.name }}</p>
              <p class="text-caption text-grey mb-4">Archivo listo para ser analizado</p>
              <v-btn
                size="x-large"
                color="primary"
                :loading="previewing"
                :disabled="previewing"
                @click="handlePreview"
                prepend-icon="mdi-file-search-outline"
                class="action-btn"
                block
              >
                {{ previewing ? 'Analizando archivo...' : 'Analizar archivo' }}
              </v-btn>
            </v-card>
          </v-card-text>
        </div>

        <!-- Step 2: Errors -->
        <div v-else-if="step === 'errors'" key="errors">
          <v-card-text class="pa-6">
            <CsvErrorTable
              :errors="previewErrors"
              :message="previewErrorMessage"
            />
          </v-card-text>

          <v-card-actions class="pa-6 pt-0 justify-center">
            <v-btn
              color="primary"
              variant="outlined"
              @click="resetToUpload"
              prepend-icon="mdi-file-upload-outline"
            >
              Subir otro archivo
            </v-btn>
            <v-btn
              color="primary"
              variant="tonal"
              class="ml-3"
              @click="downloadTemplate"
              prepend-icon="mdi-download"
            >
              Descargar plantilla
            </v-btn>
          </v-card-actions>
        </div>

        <!-- Step 3: Preview -->
        <div v-else-if="step === 'preview'" key="preview">
          <v-card-text class="pa-6">
            <CsvPreview :preview-result="previewResult" />

            <v-alert
              type="success"
              variant="tonal"
              class="mt-4 success-alert"
              density="compact"
              :text="previewResult?.message"
              title="Archivo validado correctamente"
            ></v-alert>
          </v-card-text>

          <v-card-actions class="pa-6 pt-0 justify-center">
            <v-btn
              size="large"
              color="success"
              variant="flat"
              :loading="importing"
              :disabled="importing"
              @click="handleImport"
              prepend-icon="mdi-database-import-outline"
              class="import-btn"
            >
              {{ importing ? 'Importando...' : 'Confirmar e importar' }}
            </v-btn>
            <v-btn
              color="grey"
              variant="outlined"
              class="ml-3"
              @click="resetToUpload"
              prepend-icon="mdi-close"
            >
              Cancelar
            </v-btn>
          </v-card-actions>

          <div v-if="importing" class="px-6 pb-6">
            <v-card variant="outlined" class="pa-4 text-center">
              <v-progress-circular indeterminate color="success" size="48" class="mb-3"></v-progress-circular>
              <p class="text-body-1 font-weight-medium text-grey-darken-1 mb-1">Importando usuarios...</p>
              <p class="text-caption text-grey">Por favor espera mientras se procesa el archivo.</p>
            </v-card>
          </div>
        </div>

        <!-- Step 4: Result -->
        <div v-else-if="step === 'result'" key="result">
          <ImportResultDialog
            :show="step === 'result'"
            :result="importResult || { success: false, message: csvError }"
            @close="resetToUpload"
            @retry="resetToUpload"
            @update:show="step === 'result' ? null : resetToUpload"
          />
        </div>
      </v-slide-y-reverse-transition>
    </v-card>

    <!-- History Section -->
    <div class="history-section mt-6">
      <CsvImportHistory
        :imports="history"
        :loading="historyLoading"
      />
    </div>

    <v-snackbar
      v-model="showSnackbar"
      :color="snackbarColor"
      :timeout="4000"
      location="top right"
      class="custom-snackbar"
    >
      <div class="d-flex align-center">
        <v-icon class="mr-2">
          {{ snackbarColor === 'success' ? 'mdi-check-circle' : 'mdi-alert-circle' }}
        </v-icon>
        {{ snackbarMessage }}
      </div>
    </v-snackbar>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useStore } from 'vuex'
import { csvImportApi } from '@/services/csvImportApi'
import CsvUploader from '@/components/admin/csv-import/CsvUploader.vue'
import CsvPreview from '@/components/admin/csv-import/CsvPreview.vue'
import CsvErrorTable from '@/components/admin/csv-import/CsvErrorTable.vue'
import CsvImportHistory from '@/components/admin/csv-import/CsvImportHistory.vue'
import ImportResultDialog from '@/components/admin/csv-import/ImportResultDialog.vue'

const store = useStore()

const showSnackbar = ref(false)
const snackbarMessage = ref('')
const snackbarColor = ref('success')

const steps = ['Subir archivo', 'Validar', 'Previsualizar', 'Importar']
const stepIndexMap = { upload: 0, errors: 1, preview: 2, result: 3 }

const currentStepIndex = computed(() => stepIndexMap[step.value] || 0)

const stepProgress = computed(() => {
  return ((currentStepIndex.value) / (steps.length - 1)) * 100
})

const step = computed(() => store.state.csvImport.step)
const csvFile = computed(() => store.state.csvImport.file)
const csvError = computed(() => store.state.csvImport.error)
const previewing = computed(() => store.state.csvImport.previewing)
const importing = computed(() => store.state.csvImport.importing)
const previewResult = computed(() => store.state.csvImport.previewResult)
const importResult = computed(() => store.state.csvImport.importResult)
const history = computed(() => store.state.csvImport.history)
const historyLoading = computed(() => store.state.csvImport.historyLoading)

const previewErrors = computed(() => store.state.csvImport.previewResult?.data?.errors || [])
const previewErrorMessage = computed(() => store.state.csvImport.previewResult?.message || store.state.csvImport.error)

const isStepCompleted = (index) => index < currentStepIndex.value

const getStepClass = (index) => {
  if (index < currentStepIndex.value) return 'completed'
  if (index === currentStepIndex.value) return 'active'
  return ''
}

const goToStep = (index) => {
  if (index < currentStepIndex.value && step.value === 'preview') {
    resetToUpload()
  }
}

const setFile = (file) => store.dispatch('csvImport/setFile', file)
const clearFile = () => store.dispatch('csvImport/clearFile')
const resetToUpload = () => store.dispatch('csvImport/resetToUpload')

onMounted(async () => {
  await store.dispatch('csvImport/fetchHistory')
})

const handlePreview = async () => {
  const result = await store.dispatch('csvImport/previewFile')
  if (!result.success) {
    showMessage(result.error || 'Error al analizar el archivo', 'error')
  }
}

const handleImport = async () => {
  const result = await store.dispatch('csvImport/importFile')
  if (result.success) {
    showMessage('Importación completada exitosamente', 'success')
  } else {
    showMessage(result.error || 'Error durante la importación', 'error')
  }
  await store.dispatch('csvImport/fetchHistory')
}

const downloadTemplate = () => {
  try {
    csvImportApi.downloadTemplate()
    showMessage('Plantilla descargada correctamente', 'success')
  } catch {
    showMessage('Error al descargar la plantilla', 'error')
  }
}

const showMessage = (message, color = 'success') => {
  snackbarMessage.value = message
  snackbarColor.value = color
  showSnackbar.value = true
}
</script>

<style scoped>
.csv-import-page {
  max-width: 1040px;
  margin: 0 auto;
  padding: 32px 24px;
  min-height: 100vh;
}

.page-header {
  margin-bottom: 24px;
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 16px;
}

.page-title {
  color: #0a192f;
  font-size: 1.75rem;
  font-weight: 700;
  margin-bottom: 4px;
  display: flex;
  align-items: center;
}

.page-subtitle {
  color: #64748b;
  font-size: 0.95rem;
  margin: 0;
}

.stepper {
  background: #ffffff;
  border-radius: 16px;
  padding: 24px;
  position: relative;
}

.step-indicators {
  display: flex;
  justify-content: space-between;
  position: relative;
}

.step-line {
  position: absolute;
  top: 20px;
  left: 0;
  height: 3px;
  background: linear-gradient(90deg, #0a192f, #2563eb);
  transition: width 0.4s ease;
  z-index: 1;
}

.step-indicators::before {
  content: '';
  position: absolute;
  top: 20px;
  left: 0;
  right: 0;
  height: 3px;
  background: #e2e8f0;
  z-index: 0;
}

.step-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  z-index: 2;
}

.step-item.completed .step-circle {
  cursor: pointer;
}

.step-circle {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 0.9rem;
  transition: all 0.3s ease;
  background: #e2e8f0;
  color: #94a3b8;
}

.step-item.active .step-circle {
  background: #0a192f;
  color: white;
  box-shadow: 0 4px 12px rgba(10, 25, 47, 0.3);
}

.step-item.completed .step-circle {
  background: #22c55e;
  color: white;
}

.step-label {
  margin-top: 8px;
  font-size: 0.8rem;
  color: #94a3b8;
  font-weight: 500;
  white-space: nowrap;
}

.step-item.active .step-label {
  color: #0a192f;
  font-weight: 600;
}

.step-item.completed .step-label {
  color: #22c55e;
}

.main-card {
  border-radius: 16px !important;
  border: 1px solid #e2e8f0;
}

.action-btn {
  height: 52px;
  font-size: 1rem;
  font-weight: 600;
  padding: 0 32px;
  border-radius: 12px;
  text-transform: none;
}

.import-btn {
  box-shadow: 0 4px 14px rgba(34, 197, 94, 0.35) !important;
  border-radius: 10px;
  font-weight: 600;
  text-transform: none;
  letter-spacing: 0.3px;
}

.import-btn:hover {
  box-shadow: 0 6px 20px rgba(34, 197, 94, 0.5) !important;
}

.success-alert :deep(.v-alert-title) {
  font-size: 0.95rem;
}

.history-section {
  margin-top: 24px;
}

.custom-snackbar {
  border-radius: 12px;
}

.custom-snackbar :deep(.v-snackbar__wrapper) {
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
}

@media (max-width: 768px) {
  .csv-import-page {
    padding: 16px 12px;
  }

  .header-content {
    flex-direction: column;
  }

  .page-title {
    font-size: 1.35rem;
  }

  .step-label {
    font-size: 0.65rem;
  }

  .action-btn {
    width: 100%;
  }
}
</style>
